import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

const DEFAULT_CONTAINER_CLASS = 'w-full';
const DEFAULT_INPUT_CLASS =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 pr-9 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-200';
const DEFAULT_MENU_CLASS =
  'absolute left-0 top-full z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg';
const DEFAULT_LOADING_CLASS = 'pointer-events-none absolute inset-y-0 right-3 flex items-center';

function defaultOptionClassName({ isActive }: { isActive: boolean }): string {
  return `block w-full rounded-md px-3 py-2 text-left text-sm transition ${
    isActive ? 'bg-sky-100 text-sky-900' : 'text-slate-700 hover:bg-slate-100'
  }`;
}

type NavigationMode = 'wrap' | 'clamp';

type RenderOptionArgs<TOption> = {
  option: TOption;
  index: number;
  isActive: boolean;
};

type AutocompleteProps<TOption> = {
  id?: string;
  label?: ReactNode;
  value: string;
  placeholder?: string;
  options: Array<TOption>;
  minSearchLength?: number;
  searchLength?: number;
  isLoading?: boolean;
  loadingLabel?: string;
  noResultsText?: string;
  navigationMode?: NavigationMode;
  openOnFocus?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  menuClassName?: string;
  optionClassName?: string | ((args: RenderOptionArgs<TOption>) => string);
  loadingContainerClassName?: string;
  onValueChange: (value: string) => void;
  onSelectOption: (option: TOption) => void;
  onEscapeKey?: () => void;
  getOptionKey: (option: TOption) => string;
  renderOption: (args: RenderOptionArgs<TOption>) => ReactNode;
};

export function Autocomplete<TOption>({
  id,
  label,
  value,
  placeholder,
  options,
  minSearchLength = 1,
  searchLength,
  isLoading = false,
  loadingLabel = 'Searching',
  noResultsText = 'No matching results.',
  navigationMode = 'clamp',
  openOnFocus = true,
  containerClassName = DEFAULT_CONTAINER_CLASS,
  inputClassName = DEFAULT_INPUT_CLASS,
  menuClassName = DEFAULT_MENU_CLASS,
  optionClassName = defaultOptionClassName,
  loadingContainerClassName = DEFAULT_LOADING_CLASS,
  onValueChange,
  onSelectOption,
  onEscapeKey,
  getOptionKey,
  renderOption,
}: AutocompleteProps<TOption>): ReactElement {
  const generatedId = useId();
  const inputId = id ?? `autocomplete-${generatedId}`;
  const listboxId = `${inputId}-listbox`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const optionButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const effectiveSearchLength = searchLength ?? value.trim().length;
  const canSearch = effectiveSearchLength >= minSearchLength;
  const canShowMenu = isOpen && canSearch && (options.length > 0 || Boolean(noResultsText));

  useEffect(() => {
    if (!canShowMenu) {
      setActiveIndex(-1);
    }
  }, [canShowMenu]);

  useEffect(() => {
    if (options.length === 0) {
      setActiveIndex(-1);
      return;
    }

    setActiveIndex((previousIndex) => {
      if (previousIndex < 0) {
        return -1;
      }
      return Math.min(previousIndex, options.length - 1);
    });
  }, [options]);

  useEffect(() => {
    function handleOutsidePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, []);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) {
      return;
    }

    const optionButton = optionButtonRefs.current.get(activeIndex);
    optionButton?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function selectOption(option: TOption): void {
    onSelectOption(option);
    setIsOpen(false);
    setActiveIndex(-1);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!canSearch && event.key !== 'Escape') {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      if (options.length === 0) {
        return;
      }

      setActiveIndex((previousIndex) => {
        const lastIndex = options.length - 1;
        if (navigationMode === 'clamp') {
          return previousIndex < lastIndex ? previousIndex + 1 : previousIndex;
        }

        return previousIndex >= lastIndex ? 0 : previousIndex + 1;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      if (options.length === 0) {
        return;
      }

      setActiveIndex((previousIndex) => {
        if (navigationMode === 'clamp') {
          return previousIndex > 0 ? previousIndex - 1 : -1;
        }

        return previousIndex <= 0 ? options.length - 1 : previousIndex - 1;
      });
      return;
    }

    if (event.key === 'Enter') {
      if (activeIndex < 0 || activeIndex >= options.length) {
        return;
      }

      event.preventDefault();
      selectOption(options[activeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      onEscapeKey?.();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }

  return (
    <div ref={rootRef} className={containerClassName}>
      {label ? (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-slate-700">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <input
          id={inputId}
          type="text"
          value={value}
          placeholder={placeholder}
          className={inputClassName}
          role="combobox"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={canShowMenu}
          aria-controls={listboxId}
          onChange={(event) => {
            onValueChange(event.target.value);
            setActiveIndex(-1);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (openOnFocus) {
              setIsOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
        />

        {isLoading ? (
          <div className={loadingContainerClassName}>
            <span
              className="block h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-sky-600"
              aria-label={loadingLabel}
            />
          </div>
        ) : null}

        {canShowMenu ? (
          <div ref={listRef} id={listboxId} role="listbox" className={menuClassName}>
            {options.map((option, index) => {
              const key = getOptionKey(option);
              const isActive = index === activeIndex;

              return (
                <button
                  ref={(element) => {
                    if (element) {
                      optionButtonRefs.current.set(index, element);
                    } else {
                      optionButtonRefs.current.delete(index);
                    }
                  }}
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={
                    typeof optionClassName === 'function'
                      ? optionClassName({ option, index, isActive })
                      : optionClassName
                  }
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectOption(option)}
                >
                  {renderOption({ option, index, isActive })}
                </button>
              );
            })}

            {options.length === 0 && noResultsText ? (
              <div className="px-3 py-2 text-sm text-slate-500">{noResultsText}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
